import React, { useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, Star, Check, Truck, Award, MapPin, Upload, Clock, Plus, Minus, ShoppingCart, ArrowRight } from "lucide-react";
import { PRODUCTS } from "../data/mock";
import { useCart } from "../context/CartContext";

const Step = ({ n, title, children }) => (
  <div className="mt-8">
    <div className="flex items-center gap-3 mb-4">
      <span className="w-7 h-7 rounded-full bg-rose-500 text-white text-sm font-bold flex items-center justify-center">{n}</span>
      <h3 className="text-xl font-bold text-zinc-900">{title}</h3>
    </div>
    <div>{children}</div>
  </div>
);

const OptionCard = ({ active, onClick, title, sub, badge }) => (
  <button onClick={onClick} className={`text-left border-2 rounded-lg p-4 transition-all ${active ? "border-rose-500 bg-rose-50/50 ring-1 ring-rose-200" : "border-zinc-200 bg-white hover:border-zinc-300"}`}>
    <div className="flex items-start justify-between gap-2">
      <div>
        <div className="text-sm font-bold text-zinc-900">{title}</div>
        {sub && <div className="text-xs text-zinc-500 mt-1 leading-relaxed">{sub}</div>}
      </div>
      {badge && <span className="text-[10px] font-bold uppercase tracking-wider text-rose-500 bg-rose-100 px-2 py-0.5 rounded-full">{badge}</span>}
    </div>
  </button>
);

const ProductDetailPage = () => {
  const { slug } = useParams();
  const product = PRODUCTS.find((p) => p.slug === slug);
  const { addItem } = useCart();

  const [imageIdx, setImageIdx] = useState(0);
  const [size, setSize] = useState(product?.sizes?.[0] || "");
  const [finish, setFinish] = useState(product?.finishes?.[0] || product?.sides?.[0] || "");
  const [corner, setCorner] = useState(product?.corners?.[0] || "");
  const [qtyIdx, setQtyIdx] = useState(1);
  const [design, setDesign] = useState("upload");
  const [production, setProduction] = useState("regular");
  const [stand, setStand] = useState(product?.stands?.[0]?.name || "");
  const [added, setAdded] = useState(false);

  const computed = useMemo(() => {
    if (!product) return { total: 0, base: 0, designFee: 0, qty: 0 };
    let base = product.pricing[qtyIdx]?.price ?? product.fromPrice;
    let qty = product.pricing[qtyIdx]?.qty ?? 1;
    if (product.stands) {
      const s = product.stands.find((x) => x.name === stand);
      if (s) base = s.price * qty;
    }
    const designFee = design === "designed" ? (product.designFee || 0) : 0;
    let total = base + designFee;
    if (production === "express") total *= 1.25;
    return { total: Number(total.toFixed(2)), base, designFee, qty };
  }, [product, qtyIdx, design, production, stand]);

  if (!product) {
    return <div className="max-w-7xl mx-auto px-4 py-20 text-center">Product not found. <Link to="/shop" className="text-rose-500">Back to shop</Link></div>;
  }

  const handleAdd = () => {
    addItem({
      slug: product.slug, name: product.name, image: product.image,
      size, finish, corner, stand, qty: computed.qty,
      design, production, price: computed.total,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  return (
    <main className="bg-white">
      {/* breadcrumb */}
      <div className="bg-zinc-50 border-b border-zinc-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <nav className="text-xs text-zinc-500 flex items-center gap-2">
            <Link to="/" className="hover:text-rose-500">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={`/category/${product.category}`} className="hover:text-rose-500 capitalize">{product.category.replace("-", " ")}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-zinc-900">{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10 grid lg:grid-cols-2 gap-10">
        {/* Gallery */}
        <div>
          <div className="aspect-square bg-zinc-50 rounded-xl overflow-hidden mb-4">
            <img src={product.gallery[imageIdx]} alt={product.name} className="w-full h-full object-cover" />
          </div>
          <div className="grid grid-cols-5 gap-3">
            {product.gallery.map((g, i) => (
              <button key={i} onClick={() => setImageIdx(i)} className={`aspect-square rounded-md overflow-hidden border-2 transition-all ${i === imageIdx ? "border-rose-500" : "border-transparent opacity-70 hover:opacity-100"}`}>
                <img src={g} alt="thumb" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Info + configurator */}
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="flex items-center">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className={`w-4 h-4 ${i < Math.round(product.rating) ? "fill-amber-400 text-amber-400" : "text-zinc-200"}`} />
              ))}
            </div>
            <span className="text-sm text-zinc-600">{product.rating} · {product.reviews} verified reviews</span>
          </div>
          <h1 className="text-4xl font-bold text-zinc-900 tracking-tight leading-tight">{product.name}</h1>
          <p className="text-lg text-zinc-700 mt-2 italic">{product.tagline}</p>
          <p className="text-zinc-600 leading-relaxed mt-4">{product.description}</p>

          <div className="flex flex-wrap gap-3 mt-5 text-xs">
            <span className="flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full font-medium"><Award className="w-3 h-3" /> Made in the USA</span>
            <span className="flex items-center gap-1.5 bg-rose-50 text-rose-700 px-3 py-1.5 rounded-full font-medium"><Truck className="w-3 h-3" /> Reprint promise</span>
            <span className="flex items-center gap-1.5 bg-amber-50 text-amber-700 px-3 py-1.5 rounded-full font-medium"><MapPin className="w-3 h-3" /> Free Local Pickup</span>
          </div>

          {product.sizes && (
            <Step n={1} title="Choose your size">
              <div className="grid sm:grid-cols-2 gap-3">
                {product.sizes.map((s) => (
                  <OptionCard key={s} active={size === s} onClick={() => setSize(s)} title={s.split(" ")[0]} sub={s.split(" ").slice(1).join(" ")} />
                ))}
              </div>
            </Step>
          )}

          {product.stands && (
            <Step n={1} title="Choose your stand">
              <div className="grid sm:grid-cols-2 gap-3">
                {product.stands.map((s) => (
                  <OptionCard key={s.name} active={stand === s.name} onClick={() => setStand(s.name)} title={`${s.name} · $${s.price}`} sub={s.desc} />
                ))}
              </div>
            </Step>
          )}

          {(product.finishes || product.sides) && (
            <Step n={2} title={product.sides ? "Choose side(s)" : "Choose your finish"}>
              <div className="grid sm:grid-cols-2 gap-3">
                {(product.finishes || product.sides).map((f) => (
                  <OptionCard key={f} active={finish === f} onClick={() => setFinish(f)} title={f} />
                ))}
              </div>
            </Step>
          )}

          {product.corners && (
            <Step n={3} title="Choose your corners">
              <div className="grid sm:grid-cols-2 gap-3">
                {product.corners.map((c) => (
                  <OptionCard key={c} active={corner === c} onClick={() => setCorner(c)} title={c} sub={c === "Square" ? "Sharp 90° corners." : "Soft, modern corners."} />
                ))}
              </div>
            </Step>
          )}

          <Step n={4} title="Choose your quantity">
            <div className="border border-zinc-200 rounded-lg overflow-hidden">
              <div className="grid grid-cols-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 bg-zinc-50 px-4 py-3">
                <span>Quantity</span><span>Price / unit</span><span className="text-right">Pack price</span>
              </div>
              {product.pricing.map((row, i) => (
                <button key={i} onClick={() => setQtyIdx(i)} className={`w-full grid grid-cols-3 text-sm px-4 py-3 border-t border-zinc-100 transition-colors ${qtyIdx === i ? "bg-rose-50 text-rose-700 font-semibold" : "hover:bg-zinc-50"}`}>
                  <span>{row.qty.toLocaleString()} units</span>
                  <span>${row.perUnit.toFixed(2)}</span>
                  <span className="text-right font-semibold">${row.price.toFixed(2)}</span>
                </button>
              ))}
            </div>
          </Step>

          <Step n={5} title="How would you like to design?">
            <div className="grid sm:grid-cols-2 gap-3">
              <OptionCard active={design === "designed"} onClick={() => setDesign("designed")} badge={`+$${product.designFee || 0}`} title="Design Plus will design it for me" sub="In-house designers create from your logo and brief. 2 free revisions." />
              <OptionCard active={design === "upload"} onClick={() => setDesign("upload")} badge="Free" title="I'll provide my own design" sub="PDF, AI, PSD, PNG or JPG. We'll print-readiness check it free." />
            </div>
          </Step>

          <Step n={6} title="Choose your production time">
            <div className="grid sm:grid-cols-2 gap-3">
              <OptionCard active={production === "regular"} onClick={() => setProduction("regular")} badge="Free" title={`Regular · ${product.productionDays} business days`} sub="Ships within standard turnaround of design approval." />
              <OptionCard active={production === "express"} onClick={() => setProduction("express")} badge="+25%" title="Express · 1–2 business days" sub="Skip the line — fast ship for a 25% fee." />
            </div>
          </Step>

          {/* Summary */}
          <div className="sticky bottom-4 mt-10 bg-white border border-zinc-200 rounded-xl p-5 shadow-lg">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="text-xs text-zinc-500 space-y-0.5">
                {size && <div>Size: <span className="font-semibold text-zinc-800">{size}</span></div>}
                {finish && <div>Option: <span className="font-semibold text-zinc-800">{finish}</span></div>}
                {stand && <div>Stand: <span className="font-semibold text-zinc-800">{stand}</span></div>}
                <div>Qty: <span className="font-semibold text-zinc-800">{computed.qty}</span> · Ships in <span className="font-semibold text-zinc-800">{production === "express" ? "1–2" : product.productionDays} business days</span></div>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-zinc-900">${computed.total}</div>
                <div className="text-[11px] text-zinc-500">${computed.base.toFixed(2)} + ${computed.designFee} design{production === "express" ? " + express" : ""}</div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <button onClick={handleAdd} className={`flex-1 px-6 py-3.5 rounded-md text-sm font-bold transition-colors flex items-center justify-center gap-2 ${added ? "bg-emerald-500 text-white" : "bg-zinc-900 text-white hover:bg-rose-500"}`}>
                {added ? (<><Check className="w-4 h-4" /> Added to cart</>) : (<><ShoppingCart className="w-4 h-4" /> Add to cart</>)}
              </button>
              <Link to="/cart" className="flex-1 px-6 py-3.5 rounded-md text-sm font-bold border-2 border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors flex items-center justify-center gap-2">
                Lets Print <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="max-w-7xl mx-auto px-4 mt-10">
        <div className="bg-zinc-50 rounded-xl p-8">
          <h3 className="text-xl font-bold text-zinc-900 mb-5">What's included with every order</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {product.features.map((f) => (
              <div key={f} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-rose-500 text-white flex items-center justify-center shrink-0"><Check className="w-4 h-4" /></div>
                <span className="text-sm text-zinc-700 leading-snug pt-1.5">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Other products */}
      <div className="max-w-7xl mx-auto px-4 mt-16">
        <h3 className="text-2xl font-bold text-zinc-900 mb-6 pb-4 border-b border-zinc-200">Explore other products</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {PRODUCTS.filter((p) => p.category === product.category && p.slug !== product.slug).slice(0, 4).map((p) => (
            <Link key={p.slug} to={`/product/${p.slug}`} className="group block">
              <div className="aspect-square bg-zinc-50 rounded-lg overflow-hidden border border-zinc-100">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="mt-3">
                <div className="text-sm font-semibold text-zinc-900 group-hover:text-rose-500 transition-colors">{p.name}</div>
                <div className="text-xs text-rose-500 font-semibold mt-1">From ${p.fromPrice}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </main>
  );
};

export default ProductDetailPage;
